/**
 * Prospecting: explore the Receita Federal universe, then import it as leads.
 *
 * The screen deliberately makes the operator LOOK before importing: the summary
 * and the sample come first, the import button second. Importing thousands of
 * leads sight-unseen is how a campaign goes out to the wrong universe.
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { SEGMENTOS } from '@egba-do/shared'

import { api, type Categoria, type Item, type Lista } from '../api/cliente'
import {
  Alternador,
  Aviso,
  Botao,
  Cabecalho,
  Cartao,
  Modal,
  Selecao,
  Tabela,
  Vazio,
} from '../componentes/ui'

interface Resumo {
  total: number
  comEmail: number
  comTelefone: number
  contactaveis: number
  municipios: number
  totalSocios: number
}

interface Municipio {
  municipio: string
  total: number
  contactaveis: number
}

interface Empresa {
  cnpj: string
  razaoSocial: string
  nomeFantasia: string | null
  municipio: string
  uf: string
  cnaePrincipalDesc: string
  email: string | null
  telefone: string | null
  qtdeSocios: number
}

interface ResultadoImport {
  encontrados: number
  inseridos: number
  jaExistiam: number
  descartadosSemContato: number
  descartadosSupressao: number
  duplicadosPorRaiz: number
}

/**
 * The universes come from the shared segment catalogue.
 *
 * Same list the import writes, the Empresas filter reads and the operator sees.
 * A second copy here is how the screen ends up offering a universe that no row
 * is ever tagged with.
 */
const PRESETS = SEGMENTOS.map((s) => ({
  id: s.id,
  nome: `${s.nome} — ${s.descricao}`,
  descricao: s.obrigacao,
  filtro: {
    uf: ['BA'],
    ...(s.cnaePrincipal ? { cnaePrincipal: s.cnaePrincipal } : {}),
    ...(s.cnaeSecundarioContem ? { cnaeSecundarioContem: s.cnaeSecundarioContem } : {}),
    ...(s.naturezaJuridicaContem ? { naturezaJuridicaContem: s.naturezaJuridicaContem } : {}),
  },
}))

function n(v: number): string {
  return new Intl.NumberFormat('pt-BR').format(v)
}

export function Prospeccao() {
  const [preset, setPreset] = useState(PRESETS[0]!.id)
  const [somenteContactaveis, setSomenteContactaveis] = useState(true)
  const [categoriaId, setCategoriaId] = useState('')
  const [confirmando, setConfirmando] = useState(false)
  const [resultado, setResultado] = useState<ResultadoImport | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const filtro = { ...PRESETS.find((p) => p.id === preset)!.filtro, somenteContactaveis }

  const { data: categorias } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => api.get<Lista<Categoria>>('/api/v1/admin/categorias'),
  })

  const { data: status } = useQuery({
    queryKey: ['prospeccao-status'],
    queryFn: () => api.get<{ disponivel: boolean }>('/api/v1/prospeccao/status'),
  })

  const universo = useMutation({
    mutationFn: () =>
      api.post<Item<{ resumo: Resumo; municipios: Municipio[] }>>(
        '/api/v1/prospeccao/universo',
        filtro,
      ),
    onError: (e: Error) => setErro(e.message),
  })

  const amostra = useMutation({
    mutationFn: () =>
      api.post<Lista<Empresa>>('/api/v1/prospeccao/amostra', { ...filtro, limite: 8 }),
  })

  const importar = useMutation({
    mutationFn: () =>
      api.post<Item<ResultadoImport>>('/api/v1/prospeccao/importar', {
        ...filtro,
        categoriaId,
        // O segmento é o id do preset: é ele que diz, depois, qual obrigação
        // legal aquela empresa tem.
        segmento: preset,
        limite: 10000,
      }),
    onSuccess: (r) => {
      setResultado(r.dados)
      setConfirmando(false)
    },
    onError: (e: Error) => {
      setErro(e.message)
      setConfirmando(false)
    },
  })

  function consultar() {
    setErro(null)
    setResultado(null)
    universo.mutate()
    amostra.mutate()
  }

  const resumo = universo.data?.dados.resumo

  return (
    <div className="space-y-6">
      {status && !status.disponivel && (
        <Aviso tom="perigo" titulo="Base CNPJ indisponível">
          A consulta usa a base da Receita Federal que roda nesta infraestrutura. Verifique se o
          serviço está no ar antes de tentar importar.
        </Aviso>
      )}

      <Cartao
        titulo="Universo de prospecção"
        descricao="Consulta direta à base da Receita Federal — 72,3 milhões de estabelecimentos. Confira o universo antes de importar."
      >
        <div className="space-y-5">
          <Selecao
            rotulo="Universo"
            value={preset}
            onChange={(e) => {
              setPreset(e.target.value)
              universo.reset()
              amostra.reset()
              setResultado(null)
            }}
            opcoes={PRESETS.map((p) => ({ valor: p.id, texto: p.nome }))}
            ajuda={PRESETS.find((p) => p.id === preset)?.descricao}
          />

          <Alternador
            rotulo="Somente com e-mail ou telefone"
            ajuda="Registro sem contato não serve para campanha, mas conta no tamanho do mercado."
            checked={somenteContactaveis}
            onChange={setSomenteContactaveis}
          />

          <Botao onClick={consultar} carregando={universo.isPending}>
            Consultar universo
          </Botao>
        </div>
      </Cartao>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      {resumo && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { r: 'Empresas', v: n(resumo.total), d: 'situação ativa' },
              {
                r: 'Contactáveis',
                v: n(resumo.contactaveis),
                d: `${Math.round((resumo.contactaveis / Math.max(resumo.total, 1)) * 100)}% do universo`,
              },
              { r: 'Municípios', v: n(resumo.municipios), d: 'cobertura' },
              { r: 'Sócios', v: n(resumo.totalSocios), d: 'decisores identificados' },
            ].map((i) => (
              <div key={i.r} className="cartao p-5">
                <p className="text-sm text-slate-500">{i.r}</p>
                <p className="mt-1 text-3xl font-semibold text-slate-900 tabular">{i.v}</p>
                <p className="mt-1 text-xs text-slate-400">{i.d}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Cartao titulo="Distribuição por município">
              <ul className="space-y-2">
                {universo.data?.dados.municipios.slice(0, 10).map((m) => (
                  <li key={m.municipio} className="flex items-center gap-3 text-sm">
                    <span className="w-44 truncate text-slate-700">{m.municipio}</span>
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <span
                        className="block h-full rounded-full bg-marca-600"
                        style={{
                          width: `${Math.max(3, (m.total / (universo.data!.dados.municipios[0]?.total ?? 1)) * 100)}%`,
                        }}
                      />
                    </span>
                    <span className="w-12 text-right text-slate-500 tabular">{m.total}</span>
                  </li>
                ))}
              </ul>
            </Cartao>

            <Cartao titulo="Amostra" descricao="Registros reais do universo selecionado.">
              {amostra.data?.dados.length ? (
                <ul className="space-y-3 text-sm">
                  {amostra.data.dados.map((e) => (
                    <li key={e.cnpj} className="border-b border-[color:var(--color-borda-suave)] pb-3 last:border-0">
                      <p className="truncate font-medium text-slate-900">{e.razaoSocial}</p>
                      <p className="truncate text-xs text-slate-500">
                        {e.municipio}/{e.uf} · {e.qtdeSocios} sócios ·{' '}
                        {e.email ?? e.telefone ?? 'sem contato'}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="py-6 text-center text-sm text-slate-400">Consulte o universo.</p>
              )}
            </Cartao>
          </div>

          <Cartao
            titulo="Importar para a base de leads"
            descricao="A importação aplica higiene: descarta inativas, normaliza contato, deduplica por raiz de CNPJ e respeita a lista de supressão."
          >
            <div className="flex flex-wrap items-end gap-4">
              <div className="min-w-56 flex-1">
                <Selecao
                  rotulo="Categoria de destino"
                  value={categoriaId}
                  onChange={(e) => setCategoriaId(e.target.value)}
                  opcoes={[
                    { valor: '', texto: 'Selecione…' },
                    ...(categorias?.dados ?? []).map((c) => ({ valor: c.id, texto: c.nome })),
                  ]}
                />
              </div>
              <Botao disabled={!categoriaId} onClick={() => setConfirmando(true)}>
                Importar {n(resumo.contactaveis)} registros
              </Botao>
            </div>
          </Cartao>
        </>
      )}

      {resultado && (
        <Cartao titulo="Resultado da importação">
          <Tabela>
            <Cabecalho colunas={['Etapa', 'Registros']} />
            <tbody className="divide-y divide-[color:var(--color-borda-suave)]">
              {[
                ['Encontrados na base', resultado.encontrados],
                ['Descartados sem contato válido', resultado.descartadosSemContato],
                ['Duplicados pela raiz de CNPJ', resultado.duplicadosPorRaiz],
                ['Bloqueados pela supressão', resultado.descartadosSupressao],
                ['Já existiam', resultado.jaExistiam],
              ].map(([k, v]) => (
                <tr key={String(k)}>
                  <td className="px-4 py-2.5 text-slate-600">{k}</td>
                  <td className="px-4 py-2.5 text-right text-slate-700 tabular">{n(Number(v))}</td>
                </tr>
              ))}
              <tr className="bg-marca-50/50">
                <td className="px-4 py-2.5 font-medium text-marca-800">Inseridos como leads</td>
                <td className="px-4 py-2.5 text-right font-semibold text-marca-800 tabular">
                  {n(resultado.inseridos)}
                </td>
              </tr>
            </tbody>
          </Tabela>
        </Cartao>
      )}

      <Modal
        aberto={confirmando}
        aoFechar={() => setConfirmando(false)}
        titulo="Confirmar importação"
      >
        <p className="text-sm text-slate-600">
          Serão importados até <strong>{n(resumo?.contactaveis ?? 0)}</strong> registros para a
          categoria selecionada.
        </p>
        <Aviso tom="info">
          Os leads entram com opt-in de <strong>e-mail</strong> por legítimo interesse B2B.
          WhatsApp fica desligado — esse canal exige consentimento explícito, e disparo frio é a
          principal causa de sanção e de banimento de número.
        </Aviso>
        <div className="mt-5 flex justify-end gap-3">
          <Botao variante="secundario" onClick={() => setConfirmando(false)}>
            Cancelar
          </Botao>
          <Botao carregando={importar.isPending} onClick={() => importar.mutate()}>
            Importar
          </Botao>
        </div>
      </Modal>

      {!resumo && !universo.isPending && (
        <Vazio
          titulo="Nenhuma consulta feita"
          descricao="Escolha um universo e clique em Consultar para ver o tamanho do mercado antes de importar."
        />
      )}
    </div>
  )
}
